import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '../api/billing.api';
import { useNotify } from '../context/NotifyContext';
import Modal from './Modal';

type DnsGuide = {
  fqdn: string;
  isApex: boolean;
  host: string;
  zone: string;
  type: string;
  target: string;
};

function dnsGuide(hostname: string, cnameTarget: string): DnsGuide {
  const fqdn = hostname.replace(/\.$/, '').toLowerCase();
  const labels = fqdn.split('.').filter(Boolean);
  const isApex = labels.length <= 2;
  const host = isApex ? '@' : labels[0];
  const zone = isApex ? fqdn : labels.slice(1).join('.');
  return {
    fqdn,
    isApex,
    host,
    zone,
    type: isApex ? 'ALIAS / ANAME' : 'CNAME',
    target: cnameTarget,
  };
}

const DnsRecordsGuide: React.FC<{ guide: DnsGuide; onCopy: (value: string, label: string) => void }> = ({
  guide,
  onCopy,
}) => (
  <div className="space-y-3">
    <p className="text-sm font-semibold text-nova-text">Update DNS for {guide.zone}</p>
    <ol className="list-decimal list-inside space-y-2 text-sm text-nova-text2">
      <li>
        Sign in to the DNS panel where <span className="font-mono text-nova-text">{guide.zone}</span> is managed
        (GoDaddy, Cloudflare, Namecheap, Google Domains, etc.).
      </li>
      <li>Add or edit this record. Leave proxy/CDN off at your registrar if that option exists (DNS only).</li>
      <li>Wait 5–30 minutes (sometimes up to 24 hours), then click Verify domain.</li>
    </ol>

    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-nova-text3">
            <th className="py-1 pr-3 font-medium">Type</th>
            <th className="py-1 pr-3 font-medium">Name / Host</th>
            <th className="py-1 pr-3 font-medium">Value / Points to</th>
            <th className="py-1 font-medium">TTL</th>
          </tr>
        </thead>
        <tbody className="font-mono text-nova-text">
          <tr>
            <td className="py-2 pr-3 align-top">{guide.type}</td>
            <td className="py-2 pr-3 align-top">
              <span>{guide.host}</span>
              <button type="button" className="ml-2 text-xs text-nova-sky hover:text-nova-sky2" onClick={() => onCopy(guide.host, 'Host')}>
                Copy
              </button>
            </td>
            <td className="py-2 pr-3 align-top">
              <span className="break-all">{guide.target}</span>
              <button type="button" className="ml-2 text-xs text-nova-sky hover:text-nova-sky2" onClick={() => onCopy(guide.target, 'CNAME target')}>
                Copy
              </button>
            </td>
            <td className="py-2 align-top">Auto or 3600</td>
          </tr>
        </tbody>
      </table>
    </div>

    {guide.isApex ? (
      <p className="text-sm text-nova-text3">
        Root domains often cannot use a CNAME. If your provider has no ALIAS, ANAME, or CNAME flattening, save{' '}
        <span className="font-mono text-nova-text">www.{guide.fqdn}</span> instead and redirect {guide.fqdn} to www.
      </p>
    ) : (
      <p className="text-sm text-nova-text3">
        Do not point this hostname at an IP address. The CNAME target must be exactly{' '}
        <span className="font-mono text-nova-text">{guide.target}</span>. To also use the root domain{' '}
        <span className="font-mono">{guide.zone}</span>, add an ALIAS/ANAME to the same target, or redirect it to{' '}
        <span className="font-mono">{guide.fqdn}</span>.
      </p>
    )}
  </div>
);

const DomainPanel: React.FC<{ websiteId: string; enabled: boolean }> = ({ websiteId, enabled }) => {
  const queryClient = useQueryClient();
  const { notify } = useNotify();
  const [domain, setDomain] = useState('');
  const [dnsModalOpen, setDnsModalOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['domain', websiteId],
    queryFn: () => billingApi.getDomain(websiteId),
    enabled,
  });

  const cnameTarget = data?.cnameTarget || 'customers.ontheweb.ai';
  const record = data?.customDomain;
  const hostname = record?.hostname;
  const status = record?.status || 'none';
  const guide = hostname ? dnsGuide(hostname, cnameTarget) : null;

  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    notify.success(`${label} copied.`);
  };

  const setMutation = useMutation({
    mutationFn: () => billingApi.setDomain(websiteId, domain || hostname),
    onSuccess: (result) => {
      queryClient.setQueryData(['domain', websiteId], result);
      setDomain('');
      setDnsModalOpen(true);
      notify.success('Domain saved. Add the DNS record shown, wait a few minutes, then verify.');
    },
    onError: (err: any) => notify.error(err.response?.data?.message || 'Could not save domain'),
  });

  const verifyMutation = useMutation({
    mutationFn: () => billingApi.verifyDomain(websiteId),
    onSuccess: (result) => {
      queryClient.setQueryData(['domain', websiteId], result);
      setDnsModalOpen(false);
      notify.success('Custom domain is live.');
    },
    onError: (err: any) => notify.error(err.response?.data?.message || 'Domain not verified yet'),
  });

  const removeMutation = useMutation({
    mutationFn: () => billingApi.removeDomain(websiteId),
    onSuccess: (result) => {
      queryClient.setQueryData(['domain', websiteId], result);
      setDnsModalOpen(false);
    },
  });

  if (!enabled) {
    return (
      <div className="pt-6 border-t border-nova-border">
        <h3 className="text-sm font-semibold text-nova-text mb-1">Custom domain</h3>
        <p className="text-sm text-nova-text3">
          Publish this website to connect your own domain. You can add or change DNS records after it is live.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-6 border-t border-nova-border">
      <h3 className="text-sm font-semibold text-nova-text mb-1">Custom domain</h3>
      <p className="text-sm text-nova-text3 mb-4">
        Save the hostname you want visitors to use (usually <span className="font-mono">www.yourshop.com</span>). We then
        show the exact DNS record to add at your domain registrar.
      </p>
      {isLoading ? (
        <p className="text-sm text-nova-text3">Loading domain...</p>
      ) : (
        <>
          {hostname && (
            <p className="text-sm text-nova-text mb-3">
              {hostname}{' '}
              <span className={status === 'verified' ? 'status-published' : 'status-draft'}>{status}</span>
              {status !== 'verified' && (data.sslStatus || data.hostnameStatus) ? (
                <span className="text-nova-text3">
                  {' '}
                  · SSL {data.sslStatus || 'pending'}
                  {data.hostnameStatus ? ` · hostname ${data.hostnameStatus}` : ''}
                </span>
              ) : null}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={hostname || 'www.yourshop.com'}
              className="flex-1 px-3 py-2 rounded-lg border border-nova-border bg-nova-bg text-sm font-mono"
            />
            <button type="button" className="btn-ghost" onClick={() => setMutation.mutate()} disabled={setMutation.isPending}>
              Save
            </button>
            {hostname && (
              <button type="button" className="btn-ghost" onClick={() => setDnsModalOpen(true)}>
                DNS records
              </button>
            )}
            {hostname && status !== 'verified' && (
              <button type="button" className="btn-primary" onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isPending}>
                Verify domain
              </button>
            )}
            {hostname && (
              <button type="button" className="text-sm text-nova-red" onClick={() => removeMutation.mutate()}>
                Remove
              </button>
            )}
          </div>

          {guide && status !== 'verified' && (
            <div className="mt-4 rounded-xl border border-nova-border bg-nova-bg2 p-4">
              <DnsRecordsGuide guide={guide} onCopy={copy} />
            </div>
          )}

          {guide && status === 'verified' && (
            <p className="mt-3 text-sm text-nova-text3">
              Keep the {guide.type} for <span className="font-mono text-nova-text">{guide.host}</span> pointing to{' '}
              <span className="font-mono text-nova-text">{guide.target}</span> so HTTPS stays active.
            </p>
          )}
        </>
      )}

      {guide && (
        <Modal isOpen={dnsModalOpen} onClose={() => setDnsModalOpen(false)} title="DNS records to add" maxWidthClassName="max-w-2xl">
          <DnsRecordsGuide guide={guide} onCopy={copy} />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
            <button type="button" className="btn-ghost" onClick={() => setDnsModalOpen(false)}>
              Close
            </button>
            {status !== 'verified' && (
              <button type="button" className="btn-primary" onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isPending}>
                Verify domain
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DomainPanel;
